import { Plane, Package, Calendar, Truck, Weight, Snowflake } from "lucide-react";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import {
  resolveStoredCommodity,
  resolveTemperatureRange,
} from "../common/BookingDetailUI";

countries.registerLocale(enLocale);

// Unicode regional-indicator flag emoji don't render on Windows Chrome/Edge
// (no bundled color-emoji font with flag glyphs) — use the same
// flagcdn.com image lookup already relied on elsewhere in the app
// (Newratelisting.jsx, NewBookingFlowContent.jsx) instead.
const getFlagUrl = (countryName) => {
  if (!countryName) return null;
  const code = countries.getAlpha2Code(countryName.trim(), "en");
  return code ? `https://flagcdn.com/w40/${code.toLowerCase()}.png` : null;
};

const QueryDetailHeader = ({ queryDetails }) => {
  const originAirport = queryDetails?.originPort?.originAirport || "";
  const destAirport = queryDetails?.destinationPort?.destinationAirport || "";

  const originCity = originAirport.split("-")[0]?.trim();
  const originCode = originAirport.split("-")[1]?.replace(/[()]/g, "").trim();
  const originCountryName =
    queryDetails?.originPort?.origincountryName ||
    queryDetails?.originPort?.originCountryCode ||
    "";

  const destCity = destAirport.split("-")[0]?.trim();
  const destCode = destAirport.split("-")[1]?.replace(/[()]/g, "").trim();
  const destCountryName =
    queryDetails?.destinationPort?.destinationcountryName ||
    queryDetails?.destinationPort?.destionationCountryCode ||
    "";

  const cargoDate = queryDetails?.goodsDetails?.cargoreadydate;
  // Air queries persist the commodity as goodsDetails.commodity_description;
  // the older commonditytype/cargoType fields this used to read are absent from
  // that payload, which is why the commodity pill never rendered. The shared
  // resolver covers every shape the query can arrive in.
  const commodityType = resolveStoredCommodity(queryDetails);
  // Perishables only — '' for everything else, so the pill is skipped.
  const temperatureRange = resolveTemperatureRange(queryDetails);

  const packagingType = queryDetails?.packagingDetails?.packagingType || {};
  const shipmentTotal = queryDetails?.packagingDetails?.shipmentTotal || {};

  const totalUnits = shipmentTotal?.totalunits ?? packagingType?.totalunits;
  const grossWeight = shipmentTotal?.totalweight ?? packagingType?.totalweight;
  const volumeWeight = shipmentTotal?.volumeWeight ?? packagingType?.totalvolume;
  const chargeableWeight = shipmentTotal?.chargeableWeight;
  const totalVolume = packagingType?.totalvolume;

  const needsTransport =
    queryDetails?.originPort?.originPickup ||
    queryDetails?.destinationPort?.destinationPickup;

  const originFlag = getFlagUrl(originCountryName);
  const destFlag = getFlagUrl(destCountryName);

  return (
    <div
      className="mt-6 rounded-2xl overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #fffbf0 0%, #fff8e1 100%)",
        border: "1px solid #f0dea3",
      }}
    >
      <div className="px-6 py-4 flex flex-wrap items-center justify-between gap-4">
        {/* Route */}
        <div className="flex items-center gap-5">
          {/* Origin */}
          <div className="flex items-center gap-2">
            {originFlag && (
              <img
                src={originFlag}
                alt={originCountryName}
                style={{ width: 28, height: 21, borderRadius: 3, objectFit: "cover", boxShadow: "0 0 0 1px rgba(0,0,0,0.06)" }}
              />
            )}
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-800 leading-none">
                {originCode || "—"}
              </p>
              {originCountryName && (
                <p className="text-[11px] font-semibold text-gray-500 mt-0.5 uppercase tracking-wide">
                  {originCountryName}
                </p>
              )}
            </div>
          </div>

          {/* Arrow */}
          <div className="flex items-center gap-1.5">
            <div
              style={{
                width: 32,
                borderTop: "2px dashed #FCB614",
              }}
            />
            <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center shadow-sm">
              <Plane className="w-3.5 h-3.5 text-white" />
            </div>
            <div
              style={{
                width: 32,
                borderTop: "2px dashed #FCB614",
              }}
            />
          </div>

          {/* Destination */}
          <div className="flex items-center gap-2">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-800 leading-none">
                {destCode || "—"}
              </p>
              {destCountryName && (
                <p className="text-[11px] font-semibold text-gray-500 mt-0.5 uppercase tracking-wide">
                  {destCountryName}
                </p>
              )}
            </div>
            {destFlag && (
              <img
                src={destFlag}
                alt={destCountryName}
                style={{ width: 28, height: 21, borderRadius: 3, objectFit: "cover", boxShadow: "0 0 0 1px rgba(0,0,0,0.06)" }}
              />
            )}
          </div>
        </div>

        {/* Info pills */}
        <div className="flex flex-wrap items-center gap-2">
          {cargoDate && (
            <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs text-gray-600 shadow-sm">
              <Calendar className="w-3.5 h-3.5 text-primary" />
              {cargoDate}
            </div>
          )}
          {commodityType && (
            <div
              title={commodityType}
              className="flex items-center gap-1.5 max-w-[260px] bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs text-gray-600 shadow-sm"
            >
              <Package className="w-3.5 h-3.5 text-primary flex-shrink-0" />
              <span className="truncate">{commodityType}</span>
            </div>
          )}
          {temperatureRange && (
            <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm whitespace-nowrap">
              <Snowflake className="w-3.5 h-3.5 text-sky-500 flex-shrink-0" />
              {temperatureRange}
            </div>
          )}
          {totalUnits != null && (
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs text-gray-600 shadow-sm">
              {totalUnits} Units
            </div>
          )}
          {totalVolume != null && (
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs text-gray-600 shadow-sm">
              {totalVolume} CBM
            </div>
          )}
          {grossWeight != null && (
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs text-gray-600 shadow-sm">
              <Weight className="w-3 h-3 text-gray-400" />
              G.W {grossWeight} KG
            </div>
          )}
          {volumeWeight != null && (
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs text-gray-600 shadow-sm">
              V.W {volumeWeight} KG
            </div>
          )}
          {chargeableWeight != null && (
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs text-gray-600 shadow-sm">
              C.W {chargeableWeight} KG
            </div>
          )}
          {/* <div
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium shadow-sm ${
              needsTransport
                ? "bg-blue-50 border border-blue-200 text-blue-700"
                : "bg-gray-50 border border-gray-200 text-gray-500"
            }`}
          > */}
            {/* <Truck className="w-3.5 h-3.5" /> */}
            {/* {needsTransport ? "Transport Required" : "No Transport"} */}
          {/* </div> */}
        </div>
      </div>
    </div>
  );
};

export default QueryDetailHeader;
